import { lazy, Suspense, useCallback, useEffect, useRef } from 'react';
import {
  Routes,
  Route,
  useLocation,
  Navigate,
  useNavigate,
  matchPath,
} from 'react-router-dom';
import { useQueryClient } from 'react-query';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from './utils/axios';

import { useAuth } from './contexts/auth-context';
import { useSocket } from './contexts/socket-context';
import useScrollToTop from './hooks/useScrollToTop';
import { getStoredRefreshToken, setStoredRefreshToken } from './utils/auth';
import { STATUS } from './utils/utils';
import { useTheme } from './contexts/theme-context';

import Layout from './components/Layout';
import SplashScreen from './components/SplashScreen';
import Modal from './components/Modal';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';
import Spinner from './components/Spinner';
import AddPostHeader from './components/AddPostHeader';
import GoogleAdsEntryInterstitial from './components/Ads/GoogleAdsEntryInterstitial';

import useMediaQuery from './hooks/useMediaQuery';
import { SUPPORTED_LANGUAGES } from './i18n';
import { attachNativePushListeners } from './utils/mobile-native';

const consumeAuthHashFromCasca = () => {
  if (typeof window === 'undefined') return;
  const rawHash = window.location.hash || '';
  if (!rawHash) return;
  const hashContent = rawHash.startsWith('#') ? rawHash.slice(1) : rawHash;
  if (!hashContent) return;
  let params;
  try {
    params = new URLSearchParams(hashContent);
  } catch (_) {
    return;
  }
  const auth = params.get('auth');
  if (!auth) return;
  setStoredRefreshToken(auth);
  const cleanUrl = window.location.pathname + window.location.search;
  try {
    window.history.replaceState(null, '', cleanUrl);
  } catch (_) {
    /* best-effort */
  }
};

const Home = lazy(() => import('./pages/Home'));
const LivePage = lazy(() => import('./pages/Live/LivePage'));
const Signup = lazy(() => import('./pages/Signup'));
const Signin = lazy(() => import('./pages/Signin'));
const SignupSuccess = lazy(() => import('./pages/SignupSuccess'));
const UserProfile = lazy(() => import('./pages/UserProfile'));
const Follow = lazy(() => import('./pages/Follow'));
const NoMatch = lazy(() => import('./pages/NoMatch'));
const PostDetail = lazy(() => import('./pages/PostDetail'));
const Notifications = lazy(() => import('./pages/Notifications'));
const Messages = lazy(() => import('./pages/Messages'));
const Chat = lazy(() => import('./pages/Chat'));
const Search = lazy(() => import('./pages/Search'));
const SavedItems = lazy(() => import('./pages/SavedItems'));
const ApoiadorHome = lazy(() => import('./pages/Apoiador/ApoiadorHome'));
const LegacyAdminRedirect = lazy(() =>
  import('./pages/Admin/LegacyAdminRedirect')
);
const Settings = lazy(() => import('./pages/Settings/Settings'));
const AccountInfo = lazy(() => import('./pages/Settings/AccountInfo'));
const ChangePassword = lazy(() => import('./pages/Settings/ChangePassword'));
const ChangeUserName = lazy(() => import('./pages/Settings/ChangeUserName'));
const ChangeEmail = lazy(() => import('./pages/Settings/ChangeEmail'));
const ChangeBirthDate = lazy(() => import('./pages/Settings/ChangeBirthDate'));
const ChangeWhatsApp = lazy(() => import('./pages/Settings/ChangeWhatsApp'));
const ChangeFullName = lazy(() => import('./pages/Settings/ChangeFullName'));
const ChangeLocation = lazy(() => import('./pages/Settings/ChangeLocation'));
const EmailVerificationSettings = lazy(() =>
  import('./pages/Settings/EmailVerification')
);
const EditProfile = lazy(() => import('./pages/Settings/EditProfile'));
const Display = lazy(() => import('./pages/Settings/Display'));
const Language = lazy(() => import('./pages/Settings/Language'));
const NotificationsSettings = lazy(() =>
  import('./pages/Settings/Notifications')
);
const ComposePost = lazy(() => import('./components/Posts/ComposePost'));
const ComposeQuotePost = lazy(() =>
  import('./components/Posts/ComposeQuotePost')
);
const UserPosts = lazy(() => import('./components/Posts/UserPosts'));
const PostsAndReplies = lazy(() =>
  import('./components/Posts/PostsAndReplies')
);
const LikedPosts = lazy(() => import('./components/Posts/LikedPosts'));
const UserMedia = lazy(() => import('./components/Posts/UserMedia'));
const FolloweesList = lazy(() => import('./components/FolloweesList'));
const FollowersList = lazy(() => import('./components/FollowersList'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const PublicSolicitarExclusaoConta = lazy(() =>
  import('./pages/PublicSolicitarExclusaoConta')
);
const PublicConfirmarExclusaoConta = lazy(() =>
  import('./pages/PublicConfirmarExclusaoConta')
);

const RouteFallback = () => (
  <div className="w-full h-full min-h-screen flex items-center justify-center">
    <Spinner />
  </div>
);

const App = () => {
  const { login, isAuthenticated, expiresAt, logout, user, updateUser } =
    useAuth();
  const { i18n, t } = useTranslation();
  const { setTheme, setPrimaryColor } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useSocket();
  const queryClient = useQueryClient();
  const isWidthGreaterThan640 = useMediaQuery('(min-width: 640px)');
  const { state } = location;
  const hasSyncedLanguagePreference = useRef(false);
  const hasVerifiedTokenOnMount = useRef(false);

  useScrollToTop();

  const verifyToken = useCallback(async () => {
    try {
      const storedRefreshToken = getStoredRefreshToken();
      if (!storedRefreshToken) {
        logout();
        return;
      }
      const response = await axios.post('/api/auth/refresh', {
        refreshToken: storedRefreshToken,
      });
      if (response.status === 204) {
        logout();
        return;
      }
      const {
        // eslint-disable-next-line no-shadow
        user,
        accessToken: token,
        refreshToken: nextRefreshToken,
        expiresAt: refreshedExpiresAt,
      } = response.data;
      login(user, token, nextRefreshToken, refreshedExpiresAt);
    } catch (_error) {
      logout();
    }
  }, [login, logout]);

  useEffect(() => {
    if (hasVerifiedTokenOnMount.current) return;
    hasVerifiedTokenOnMount.current = true;
    consumeAuthHashFromCasca();
    verifyToken();
  }, [verifyToken]);

  useEffect(() => {
    if (!isAuthenticated) return;
    attachNativePushListeners();
  }, [isAuthenticated]);

  useEffect(() => {
    let verifyTokenTimer;
    if (isAuthenticated) {
      const delay = Math.max(
        0,
        new Date(expiresAt).getTime() - Date.now() - 10 * 1000
      );
      if (delay > 0) {
        verifyTokenTimer = setTimeout(() => {
          verifyToken();
        }, delay);
      }
    }
    return () => {
      if (isAuthenticated && verifyTokenTimer) {
        clearTimeout(verifyTokenTimer);
      }
    };
  }, [isAuthenticated, expiresAt, verifyToken]);

  useEffect(() => {
    if (!socket) return undefined;

    const handleNotification = () => {
      queryClient.invalidateQueries('notifications');
    };

    const handleNewMessage = ({ chatId }) => {
      queryClient.invalidateQueries(['chat', chatId]);
      queryClient.invalidateQueries(['chat', String(chatId)]);
      // Only invalidate messages list if the page is currently mounted
      queryClient.invalidateQueries({
        queryKey: 'messages',
        refetchActive: true,
      });
    };

    socket.on('new notification', handleNotification);
    socket.on('new message', handleNewMessage);

    return () => {
      socket.off('new notification', handleNotification);
      socket.off('new message', handleNewMessage);
    };
  }, [socket, queryClient]);

  useEffect(() => {
    if (!isAuthenticated || !user) return;
    if (user.preferredLanguage) {
      i18n.changeLanguage(user.preferredLanguage).catch(() => {});
    }
    if (user.preferredTheme) {
      setTheme(user.preferredTheme);
    }
    if (user.preferredAccent) {
      setPrimaryColor(user.preferredAccent);
    }
  }, [i18n, isAuthenticated, setPrimaryColor, setTheme, user]);

  useEffect(() => {
    if (!isAuthenticated) {
      hasSyncedLanguagePreference.current = false;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (!isAuthenticated || !user || user.preferredLanguage) return;
    if (hasSyncedLanguagePreference.current) return;

    const normalizedLanguage = (i18n.resolvedLanguage || i18n.language || 'pt')
      .toString()
      .slice(0, 2)
      .toLowerCase();
    const isSupportedLanguage = SUPPORTED_LANGUAGES.some(
      (lang) => lang.code === normalizedLanguage
    );
    if (!isSupportedLanguage) return;

    hasSyncedLanguagePreference.current = true;
    axios
      .patch('/api/users/my/preferences', {
        preferredLanguage: normalizedLanguage,
      })
      .then((response) => {
        updateUser(response.data.user);
      })
      .catch(() => {
        hasSyncedLanguagePreference.current = false;
      });
  }, [i18n.language, i18n.resolvedLanguage, isAuthenticated, updateUser, user]);

  return (
    <ErrorBoundary>
      {isAuthenticated ? <GoogleAdsEntryInterstitial /> : null}
      <Suspense fallback={<RouteFallback />}>
        <div>
          <Routes location={state?.backgroundLocation || location}>
            <Route
              path="/"
              element={
                <RequireAuth redirectTo="/signup">
                  <Layout />
                </RequireAuth>
              }
            >
              <Route
                index
                element={
                  <RequireAuth redirectTo="/signup">
                    <Navigate to="home" />
                  </RequireAuth>
                }
              />
              <Route
                path="home"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Home />
                  </RequireAuth>
                }
              />
              <Route
                path="live"
                element={
                  <RequireAuth redirectTo="/signup">
                    <LivePage />
                  </RequireAuth>
                }
              />
              <Route
                path="search"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Search />
                  </RequireAuth>
                }
              />
              <Route
                path="notifications"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Notifications />
                  </RequireAuth>
                }
              />
              <Route
                path="apoiador"
                element={
                  <RequireAuth redirectTo="/signup">
                    <ApoiadorHome />
                  </RequireAuth>
                }
              />
              <Route
                path="assinaturas"
                element={
                  <RequireAuth redirectTo="/signup">
                    <ApoiadorHome />
                  </RequireAuth>
                }
              />
              <Route
                path="saved"
                element={
                  <RequireAuth redirectTo="/signup">
                    <SavedItems />
                  </RequireAuth>
                }
              />
              <Route path="admin" element={<Navigate to="/admin" replace />} />
              <Route
                path="settings"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Settings />
                  </RequireAuth>
                }
              >
                {isWidthGreaterThan640 && (
                  <>
                    <Route
                      index
                      element={
                        <RequireAuth redirectTo="/signup">
                          <Navigate to="account" />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="account"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <AccountInfo />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="password"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangePassword />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="username"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangeUserName />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="email"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangeEmail />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="birthdate"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangeBirthDate />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="whatsapp"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangeWhatsApp />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="full-name"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangeFullName />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="location"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <ChangeLocation />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="email-verification"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <EmailVerificationSettings />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="notifications"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <NotificationsSettings />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="display"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <Display />
                        </RequireAuth>
                      }
                    />
                    <Route
                      path="language"
                      element={
                        <RequireAuth redirectTo="/signup">
                          <Language />
                        </RequireAuth>
                      }
                    />
                  </>
                )}
                <Route
                  path="profile"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Navigate
                        to={`/${user.username}`}
                        state={{
                          from: location,
                        }}
                      />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route
                path="messages"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Messages />
                  </RequireAuth>
                }
              >
                {isWidthGreaterThan640 && (
                  <Route
                    path=":chatId"
                    element={<Chat key={location.pathname} />}
                  />
                )}
              </Route>
              <Route
                path="/:username"
                element={
                  <RequireAuth redirectTo="/signup">
                    <UserProfile />
                  </RequireAuth>
                }
              >
                <Route
                  index
                  element={
                    <RequireAuth redirectTo="/signup">
                      <UserPosts />
                    </RequireAuth>
                  }
                />
                <Route
                  path="posts"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <UserPosts />
                    </RequireAuth>
                  }
                />
                <Route
                  path="with_replies"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <PostsAndReplies />
                    </RequireAuth>
                  }
                />
                <Route
                  path="likes"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <LikedPosts />
                    </RequireAuth>
                  }
                />
                <Route
                  path="media"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <UserMedia />
                    </RequireAuth>
                  }
                />
              </Route>
              <Route
                path="/:username/post/:postId"
                element={<PostDetail key={location.pathname} />}
              />
              <Route
                path="/:username/list"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Follow />
                  </RequireAuth>
                }
              >
                <Route path="followers" element={<FollowersList />} />
                <Route path="following" element={<FolloweesList />} />
              </Route>
              <Route
                path="compose/post"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Navigate
                      to="/home"
                      state={{
                        from: location,
                      }}
                    />
                  </RequireAuth>
                }
              />
              <Route
                path="compose/repost/:postId"
                element={
                  <RequireAuth redirectTo="/signup">
                    <Navigate
                      to="/home"
                      state={{
                        from: location,
                      }}
                    />
                  </RequireAuth>
                }
              />
            </Route>
            <Route
              path="/admin/*"
              element={
                <RequireAdmin redirectTo="/home">
                  <LegacyAdminRedirect />
                </RequireAdmin>
              }
            />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/politica-de-privacidade" element={<Privacy />} />
            <Route
              path="/solicitar-exclusao-de-conta"
              element={<PublicSolicitarExclusaoConta />}
            />
            <Route
              path="/confirmar-exclusao-de-conta"
              element={<PublicConfirmarExclusaoConta />}
            />
            <Route
              path="/signup"
              element={
                <RedirectIfLoggedIn redirectTo="/">
                  <Signup />
                </RedirectIfLoggedIn>
              }
            />
            <Route
              path="/signin"
              element={
                <RedirectIfLoggedIn redirectTo="/">
                  <Signin />
                </RedirectIfLoggedIn>
              }
            />
            <Route
              path="/signup/success"
              element={
                <RequireAuth redirectTo="/signup">
                  <SignupSuccess />
                </RequireAuth>
              }
            />
            <Route path="*" element={<NoMatch />} />
          </Routes>
          {state?.backgroundLocation &&
            matchPath('/compose/post', location.pathname) && (
              <Routes>
                <Route
                  path="/compose/post"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() =>
                          navigate(`${state.backgroundLocation.pathname}`, {
                            replace: true,
                          })
                        }
                        rounded={isWidthGreaterThan640}
                        customHeader={
                          <AddPostHeader
                            onDismiss={() =>
                              navigate(`${state.backgroundLocation.pathname}`, {
                                replace: true,
                              })
                            }
                          />
                        }
                      >
                        <ComposePost />
                      </Modal>
                    </RequireAuth>
                  }
                />
              </Routes>
            )}
          {state?.backgroundLocation &&
            matchPath('/compose/repost/:postId', location.pathname) && (
              <Routes>
                <Route
                  path="/compose/repost/:postId"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() =>
                          navigate(`${state.backgroundLocation.pathname}`, {
                            replace: true,
                          })
                        }
                        rounded={isWidthGreaterThan640}
                        customHeader={
                          <AddPostHeader
                            title={t('post.quoteRepostTitle')}
                            onDismiss={() =>
                              navigate(`${state.backgroundLocation.pathname}`, {
                                replace: true,
                              })
                            }
                          />
                        }
                      >
                        <ComposeQuotePost />
                      </Modal>
                    </RequireAuth>
                  }
                />
              </Routes>
            )}
          {state?.backgroundLocation &&
            matchPath('/settings/profile', location.pathname) && (
              <Routes>
                <Route
                  path="/settings/profile"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() =>
                          navigate(`${state.backgroundLocation.pathname}`, {
                            replace: true,
                          })
                        }
                        rounded={!!isWidthGreaterThan640}
                        headerVisible={false}
                      >
                        <EditProfile />
                      </Modal>
                    </RequireAuth>
                  }
                />
              </Routes>
            )}
          {!isWidthGreaterThan640 &&
            matchPath('/messages/:chatId', location.pathname) && (
              <Routes>
                <Route
                  path="/messages/:chatId"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <Chat key={location.pathname} />
                      </Modal>
                    </RequireAuth>
                  }
                />
              </Routes>
            )}
          {!isWidthGreaterThan640 &&
            !matchPath('/settings', location.pathname) &&
            !matchPath('/settings/profile', location.pathname) &&
            matchPath('/settings/*', location.pathname) && (
              <Routes>
                <Route
                  path="/settings/account"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <AccountInfo />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/password"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangePassword />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/username"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangeUserName />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/email"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangeEmail />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/birthdate"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangeBirthDate />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/whatsapp"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangeWhatsApp />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/full-name"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangeFullName />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/location"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <ChangeLocation />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/email-verification"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <EmailVerificationSettings />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/notifications"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <NotificationsSettings />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/display"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <Display />
                      </Modal>
                    </RequireAuth>
                  }
                />
                <Route
                  path="/settings/language"
                  element={
                    <RequireAuth redirectTo="/signup">
                      <Modal
                        isOpen
                        onDismiss={() => navigate(-1)}
                        headerVisible={false}
                      >
                        <Language />
                      </Modal>
                    </RequireAuth>
                  }
                />
              </Routes>
            )}
        </div>
      </Suspense>
      <CookieConsent />
    </ErrorBoundary>
  );
};

const RequireAuth = ({ children, redirectTo }) => {
  const { isAuthenticated, status } = useAuth();
  const location = useLocation();

  if (status === STATUS.PENDING) return <SplashScreen />;

  return isAuthenticated ? (
    children
  ) : (
    <Navigate to={redirectTo} state={{ from: location }} />
  );
};

const RedirectIfLoggedIn = ({ children, redirectTo }) => {
  const { isAuthenticated, status } = useAuth();
  const location = useLocation();

  if (status === STATUS.PENDING) return <SplashScreen />;

  return isAuthenticated ? (
    <Navigate to={location.state?.from?.pathname || redirectTo} />
  ) : (
    children
  );
};

const RequireAdmin = ({ children, redirectTo }) => {
  const { isAuthenticated, status, user } = useAuth();
  const location = useLocation();

  if (status === STATUS.PENDING) return <SplashScreen />;

  if (!isAuthenticated) {
    return <Navigate to="/signin" state={{ from: location }} />;
  }

  return user.isAdmin ? children : <Navigate to={redirectTo} replace />;
};

RequireAuth.propTypes = {
  children: PropTypes.element.isRequired,
  redirectTo: PropTypes.string.isRequired,
};

RedirectIfLoggedIn.propTypes = {
  children: PropTypes.element.isRequired,
  redirectTo: PropTypes.string.isRequired,
};

RequireAdmin.propTypes = {
  children: PropTypes.element.isRequired,
  redirectTo: PropTypes.string.isRequired,
};

export default App;
